import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import AnimatedBackground from '../components/AnimatedBackground';
import Footer from '../components/Footer';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import { RiGlobalLine } from "react-icons/ri";

const About = () => {
  // Scroll reveal logic (same behavior as Home)
  useEffect(() => {
    const handleScroll = () => {
      const reveals = document.querySelectorAll('.reveal');
      for (let i = 0; i < reveals.length; i++) {
        const windowHeight = window.innerHeight;
        const revealTop = reveals[i].getBoundingClientRect().top;
        const revealPoint = 150;
        if (revealTop < windowHeight - revealPoint) {
          reveals[i].classList.add('active');
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const [activeTab, setActiveTab] = useState('mission');

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-indigo-950 to-black">
      <AnimatedBackground />
      <Navbar />

      {/* Hero */}
      <section className="relative isolate overflow-hidden pt-28 pb-8">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white">
            About Cloudima
          </h1>
          <p className="mt-6 text-lg leading-8 text-gray-300">
            Building the future of cloud: visual, accessible, and no-code.
          </p>
        </div>
      </section>

      {/* Intro: 3 short paragraphs with side images */}
      <section className="mx-auto max-w-6xl px-6 pb-8">
        {/* Row 1 */}
        <div className="reveal fade-bottom grid md:grid-cols-2 gap-8 pb-4 items-center">
          <div className="order-1 md:order-1">
            <h2 className="text-2xl font-semibold text-white">From visual design to real infrastructure</h2>
            <p className="mt-3 text-gray-300">
              We started Cloudima to remove complexity from cloud operations. Your visual designs become production-grade infrastructure as code — without writing a single line.
            </p>
          </div>
          <div className="order-2 md:order-2">
                          <img
                src="images/terraform.png"
                alt="Terraform code snippet placeholder"
                className="w-full -60px h-auto rounded-xl ring-1 ring-white/10 shadow-lg object-cover"
              />
          </div>
        </div>

        {/* Row 2 */}
        <div className="reveal fade-bottom grid md:grid-cols-2 gap-8 pb-4 items-center">
          <div className="order-2 md:order-1">
                          <img
                src="images/4-torres-madrid.jpg"
                alt="Madrid Four Towers"
                className="w-full -60px h-auto rounded-xl ring-1 ring-white/10 shadow-lg object-cover"
              />
          </div>
          <div className="order-1 md:order-2">
            <h2 className="text-2xl font-semibold text-white">Enterprise-grade foundations</h2>
            <p className="mt-3 text-gray-300">
              Our roots in top consulting firms shaped our standard for security, governance, and scale — so teams keep control without bottlenecks.
            </p>
          </div>
        </div>

        {/* Row 3 */}
        <div className="reveal fade-bottom grid md:grid-cols-2 gap-8 items-center">
          <div className="order-1 md:order-1">
            <h2 className="text-2xl font-semibold text-white">One canvas for any cloud</h2>
            <p className="mt-3 text-gray-300">
              Design, validate costs and security, and deploy to AWS or Azure with version control. Cloudima brings your teams together — technical and non-technical.
            </p>
          </div>
          <div className="order-2 md:order-2">
                          <img
                src="images/Cloudima.png"
                alt="Cloudima platform UI placeholder"
                className="w-full -60px h-auto rounded-xl ring-1 ring-white/10 shadow-lg object-cover"
              />
          </div>
        </div>
      </section>

      {/* Timeline - History */}
      <section className="mx-auto max-w-5xl px-6 py-4 md:py-8 reveal fade-bottom">
        <h2 className="text-2xl font-bold text-white text-center">Our Journey</h2>
        <div className="relative mt-6 md:mt-10">
          {/* Vertical line */}
          <div className="absolute left-1/2 -translate-x-1/2 h-full w-px bg-white/10" />

          <div className="space-y-6 md:space-y-4">
            {/* 2023 */}
            <div className="grid grid-cols-1 md:grid-cols-2 items-center">
              <div className="hidden md:block" />
              <div className="relative md:pl-10 text-center md:text-left pt-12 md:pt-0">
                <div className="absolute top-2 left-1/2 -translate-x-1/2 h-10 w-10 md:h-10 md:w-10 rounded-full bg-indigo-600/90 ring-4 ring-indigo-500/30 flex items-center justify-center text-white md:right-auto md:translate-x-0 md:-left-5">
                  <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <p className="text-sm text-indigo-300 font-semibold">2023</p>
                <h3 className="text-white font-semibold mt-1">Cloudima Idea</h3>
                <p className="text-gray-300 mt-2">While working at one of the big four, we detected the need for a visual tool to design and deploy cloud infrastructure in complex projects.</p>
              </div>
            </div>

            {/* 2024 Q4 */}
            <div className="grid grid-cols-1 md:grid-cols-2 items-center">
              <div className="relative md:pr-10 md:text-right text-center pt-12 md:pt-0">
                <div className="absolute top-2 left-1/2 -translate-x-1/2 h-10 w-10 md:h-10 md:w-10 rounded-full bg-indigo-600/90 ring-4 ring-indigo-500/30 flex items-center justify-center text-white md:left-auto md:translate-x-0 md:-right-5">
                  <CloudQueueIcon className="w-5 h-5 text-white" />
                </div>
                <p className="text-sm text-indigo-300 font-semibold">2024 (Q4)</p>
                <h3 className="text-white font-semibold mt-1">MVP Creation</h3>
                <p className="text-gray-300 mt-2">First prototype with drag & drop editor, automatic Terraform generation and Azure deployments; validated in simulated environments.</p>
              </div>
              <div className="hidden md:block" />
            </div>

            {/* 2025 Q1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 items-center">
              <div className="hidden md:block" />
              <div className="relative md:pl-10 text-center md:text-left pt-12 md:pt-0">
                <div className="absolute top-2 left-1/2 -translate-x-1/2 h-10 w-10 md:h-10 md:w-10 rounded-full bg-indigo-600/90 ring-4 ring-indigo-500/30 flex items-center justify-center text-white md:right-auto md:translate-x-0 md:-left-5">
                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg">
                    <g transform="translate(0.000000,256.000000) scale(0.100000,-0.100000)" fill="currentColor" stroke="none">
                      <path d="M1185 2393 c-276 -33 -500 -134 -670 -300 -156 -152 -265 -344 -324 -568 -39 -150 -32 -388 15 -553 67 -232 153 -327 217 -240 24 32 23 35 -43 192 -54 129 -70 205 -70 352 0 132 20 230 77 374 59 152 215 344 355 438 218 146 523 201 772 138 111 -29 237 -83 316 -136 85 -57 87 -62 44 -99 -53 -47 -104 -118 -122 -169 -24 -71 -11 -197 42 -410 54 -214 59 -290 24 -342 -37 -54 -71 -73 -138 -78 -78 -6 -103 1 -150 44 -55 50 -75 115 -54 183 20 68 48 99 120 131 80 37 103 73 78 123 -9 17 -72 86 -140 153 -68 67 -124 124 -124 127 0 4 24 14 54 22 114 33 151 84 104 140 l-24 27 -73 -12 c-120 -20 -250 -56 -271 -75 -22 -20 -26 -55 -9 -88 6 -12 72 -84 147 -159 l136 -137 -41 -44 c-128 -139 -146 -310 -48 -445 73 -100 146 -137 271 -137 73 0 88 3 150 35 50 26 80 50 115 93 78 97 85 157 43 364 -14 70 -28 131 -30 134 -2 3 -13 48 -24 100 -25 112 -27 181 -5 232 18 43 81 92 137 108 115 30 101 117 -35 225 -179 143 -341 218 -537 250 -78 13 -212 16 -285 7z"/>
                      <path d="M2165 1848 c-48 -27 -46 -55 10 -193 67 -167 78 -220 77 -370 -1 -197 -47 -356 -150 -520 -85 -134 -167 -220 -284 -298 -134 -89 -283 -141 -449 -157 -120 -11 -260 1 -363 31 -123 36 -197 75 -350 181 -5 3 18 32 52 63 87 80 113 139 113 247 0 91 -12 156 -67 370 -56 216 -43 295 56 346 111 56 240 3 279 -116 13 -38 12 -49 -4 -100 -20 -65 -67 -116 -115 -127 -46 -10 -90 -52 -90 -85 0 -37 38 -84 171 -212 l112 -106 -72 -23 c-87 -28 -111 -48 -111 -93 0 -64 41 -78 153 -52 34 8 95 23 136 32 86 20 111 40 111 90 0 32 -13 48 -143 180 -78 80 -143 149 -145 153 -2 5 8 17 22 28 42 34 104 132 115 184 25 115 5 204 -64 291 -52 66 -110 100 -201 118 -110 22 -233 -25 -316 -120 -92 -105 -96 -171 -29 -455 62 -263 65 -287 52 -342 -17 -67 -41 -95 -108 -127 -32 -15 -69 -33 -83 -40 -38 -18 -48 -59 -25 -103 32 -58 199 -189 324 -253 66 -33 207 -80 288 -96 157 -30 378 -14 558 41 289 88 518 291 668 590 83 167 124 423 98 619 -20 149 -87 338 -143 401 -32 37 -50 42 -83 23z"/>
                    </g>
                  </svg>
                </div>
                <p className="text-sm text-indigo-300 font-semibold">2025 (Q1)</p>
                <h3 className="text-white font-semibold mt-1">Espacio_RES Entry</h3>
                <p className="text-gray-300 mt-2">Selected by one of the main accelerators in Seville and Andalusia to boost our strategy and growth.</p>
              </div>
            </div>

            {/* 2025 Q2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 items-center">
              <div className="relative md:pr-10 md:text-right text-center pt-12 md:pt-0">
                <div className="absolute top-2 left-1/2 -translate-x-1/2 h-10 w-10 md:h-10 md:w-10 rounded-full bg-indigo-600/90 ring-4 ring-indigo-500/30 flex items-center justify-center text-white md:left-auto md:translate-x-0 md:-right-5">
                  <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="done" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-sm text-indigo-300 font-semibold">2025 (Q2)</p>
                <h3 className="text-white font-semibold mt-1">Awards and Early Adopters</h3>
                <p className="text-gray-300 mt-2">Winners of ETSI-Emprende and University of Seville awards; we incorporated the first technical users with AWS and Azure deployments.</p>
              </div>
              <div className="hidden md:block" />
            </div>

            {/* 2025 Q3 */}
            <div className="grid grid-cols-1 md:grid-cols-2 items-center">
              <div className="hidden md:block" />
              <div className="relative md:pl-10 text-center md:text-left pt-12 md:pt-0">
                <div className="absolute top-2 left-1/2 -translate-x-1/2 h-10 w-10 md:h-10 md:w-10 rounded-full bg-indigo-600/90 ring-4 ring-indigo-500/30 flex items-center justify-center text-white md:right-auto md:translate-x-0 md:-left-5">
                  <RiGlobalLine className="w-5 h-5 text-white" />
                </div>
                <p className="text-sm text-indigo-300 font-semibold">2025 (Q3)</p>
                <h3 className="text-white font-semibold mt-1">Babson College and First Clients</h3>
                <p className="text-gray-300 mt-2">Training at Babson College (Boston, USA) and first commercial agreements with consulting firms and cloud integrators.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission / Vision - Interactive */}
      <section className="mx-auto max-w-4xl px-6 py-4 md:py-8 reveal fade-bottom">
        <div className="rounded-2xl p-2 bg-slate-800/40 ring-1 ring-slate-700/50 backdrop-blur-sm">
          <div className="flex p-1 rounded-xl bg-slate-900/60">
            <button
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'mission' ? 'bg-indigo-500 text-white' : 'text-gray-300 hover:text-white'}`}
              onClick={() => setActiveTab('mission')}
            >
              Mission
            </button>
            <button
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'vision' ? 'bg-indigo-500 text-white' : 'text-gray-300 hover:text-white'}`}
              onClick={() => setActiveTab('vision')}
            >
              Vision
            </button>
          </div>
          <div className="p-6">
            {activeTab === 'mission' ? (
              <div>
                <h3 className="text-2xl font-bold text-white">Democratize access to the cloud</h3>
                <p className="mt-3 text-gray-300">Cloudima enables any team — regardless of technical maturity — to build and operate infrastructure in AWS or Azure visually, securely and automatically.</p>
              </div>
            ) : (
              <div>
                <h3 className="text-2xl font-bold text-white">The no‑code standard for cloud</h3>
                <p className="mt-3 text-gray-300">We’re redefining how cloud is built by combining DevOps best practices, visual design and AI — so creating cloud environments feels as natural as building an app.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Founders */}
      <section className="mx-auto max-w-6xl px-6 py-4 md:py-8 reveal fade-bottom">
        <h2 className="text-2xl font-bold text-white text-center">Founders</h2>
        <div className="mt-8 grid gap-8 md:grid-cols-2">
          <a
            href="https://www.linkedin.com/in/ismael-martin-cloudima/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Ismael on LinkedIn"
            className="relative rounded-2xl p-8 bg-slate-800/40 ring-1 ring-slate-700/50 backdrop-blur-sm text-center block group hover:ring-indigo-500/40 transition"
          >
            <div className="absolute top-3 right-3 text-[#0A66C2] group-hover:text-[#0A66C2]">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M19 0h-14c-2.76 0-5 2.24-5 5v14c0 2.76 2.24 5 5 5h14c2.76 0 5-2.24 5-5v-14c0-2.76-2.24-5-5-5zm-11 19h-3v-10h3v10zm-1.5-11.25c-.966 0-1.75-.784-1.75-1.75s.784-1.75 1.75-1.75 1.75.784 1.75 1.75-.784 1.75-1.75 1.75zm13.5 11.25h-3v-5.5c0-1.38-1.12-2.5-2.5-2.5s-2.5 1.12-2.5 2.5v5.5h-3v-10h3v1.5c.69-.94 1.79-1.5 3-1.5 2.21 0 4 1.79 4 4v6z"></path>
              </svg>
            </div>
            <img
              src="images/team/Ismael-Martin-Saldana.jfif"
              alt="Ismael Martín Saldaña"
              loading="lazy"
              className="mx-auto w-32 h-32 md:w-36 md:h-36 rounded-full object-cover ring-2 ring-white/10"
            />
            <h3 className="mt-5 text-xl font-semibold text-white">Ismael Martín Saldaña</h3>
            <p className="text-indigo-300">CEO & Cloud Architect</p>
          </a>

          <a
            href="https://www.linkedin.com/in/miguel-angel-morales-cloudima/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Miguel on LinkedIn"
            className="relative rounded-2xl p-8 bg-slate-800/40 ring-1 ring-slate-700/50 backdrop-blur-sm text-center block group hover:ring-indigo-500/40 transition"
          >
            <div className="absolute top-3 right-3 text-[#0A66C2] group-hover:text-[#0A66C2]">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M19 0h-14c-2.76 0-5 2.24-5 5v14c0 2.76 2.24 5 5 5h14c2.76 0 5-2.24 5-5v-14c0-2.76-2.24-5-5-5zm-11 19h-3v-10h3v10zm-1.5-11.25c-.966 0-1.75-.784-1.75-1.75s.784-1.75 1.75-1.75 1.75.784 1.75 1.75-.784 1.75-1.75 1.75zm13.5 11.25h-3v-5.5c0-1.38-1.12-2.5-2.5-2.5s-2.5 1.12-2.5 2.5v5.5h-3v-10h3v1.5c.69-.94 1.79-1.5 3-1.5 2.21 0 4 1.79 4 4v6z"></path>
              </svg>
            </div>
            <img
              src="images/team/miguel-angel-morales.jfif"
              alt="Miguel Ángel Morales"
              loading="lazy"
              className="mx-auto w-32 h-32 md:w-36 md:h-36 rounded-full object-cover ring-2 ring-white/10"
            />
            <h3 className="mt-5 text-xl font-semibold text-white">Miguel Ángel Morales</h3>
            <p className="text-indigo-300">CTO & DevOps Lead</p>
          </a>
        </div>
      </section>


      {/* CTA final */}
      <section className="mx-auto max-w-6xl px-6 py-8 md:py-16">
        <div className="text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">Ready to join the future of cloud?</h2>
          <p className="mt-6 text-lg leading-8 text-gray-300">We’re growing with partners, integrators and early adopters — expanding with generative AI and industry templates.</p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="mailto:contact@cloudima.org?subject=Partnership%20opportunities"
              className="rounded-md bg-white/90 px-4 py-2.5 text-sm font-semibold text-indigo-900 shadow-sm hover:bg-white"
            >
              Explore partnerships
            </a>
            <a
              href="/demo"
              className="rounded-md bg-indigo-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-400"
            >
              Request a personalized demo
            </a>
            <a
              href="https://www.linkedin.com/company/cloudima"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-semibold leading-6 text-white hover:text-indigo-300"
            >
              Follow us on LinkedIn
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;



